import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Breadcrumbs,
  Flex,
  FlexColumn,
  FloatingActionsMenu,
} from '@elementary/common-ui/components';
import Table, {
  TableEmptyState,
  TableFilters,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import { useBreadcrumbs } from '@elementary/common-ui/hooks/useBreadcrumbs';
import useNavigateWithEnvParam from '@elementary/common-ui/hooks/useNavigateWithEnvParam';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import { ModelRuns } from '@elementary/common-ui/types';
import { Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { MODEL_RUNS_TABLE_FILTERS } from '@elementary/common-ui/constants/urlParams';
import Humanize from 'humanize-plus';
import { getValue, isInSaaSMode } from '@elementary/common-ui/utils/common';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import useReport from '@/shared/hooks/useReport';
import useModelRunsTableColumns from '../hooks/useModelRunsTableColumns';
import useModelRunsTableData from '../hooks/useModelRunsTableData';
import ModelRunsExpandedRow from './ModelRunsExpandedRow';

const DaysBack = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray2};
  background: ${({ theme }) => theme.palette.common.gray3};
  padding: 8px 16px;
  border-radius: 4px;
`;

const TableWrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.common.white};
  overflow: hidden;
  width: 100%;
  height: 100%;
  gap: 24px;
`;

const TableHeader = styled(Flex)`
  gap: 24px;
  padding-bottom: 16px;
  width: 100%;
  justify-content: space-between;
`;

const ModelRunsPage = () => {
  const isSaaS = isInSaaSMode();
  const { reportData } = useReport();
  const { data: account } = useAccount();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Model Performance',
  });

  const topContentRef = useRef<HTMLDivElement>(null);
  const { id: selectedRunId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigateWithEnvParam();
  const { openPanel } = useAiChatPanel();

  const {
    tableData,
    isTableAssetsFetching,
    fetchNextPage,
    hasNextPage,
    filterOptions,
    isFilterOptionsFetching,
  } = useModelRunsTableData({
    reportData,
    search: isSaaS ? debouncedSearch : undefined,
  });
  const columns = useModelRunsTableColumns({
    tableData,
    isSaaS,
    filterOptions,
  });

  const isLoading = useMemo(
    () =>
      isSaaS ? isTableAssetsFetching : isTableAssetsFetching || !reportData,
    [isSaaS, isTableAssetsFetching, reportData],
  );

  useViewLoadTracking({
    name: 'Model Performance',
    loadDeps: [tableData],
  });

  useEffect(() => {
    if (selectedRunId && !isLoading) {
      const isSelectedRunExists = tableData.some(
        (modelRun) => modelRun.unique_id === selectedRunId,
      );

      if (!isSelectedRunExists) {
        navigate(`/report/model-runs?${searchParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunId, tableData, isLoading]);

  const handleOpenAiChat = useCallback(
    (rowData: ModelRuns) => {
      openPanel({
        startMessage: `How can I optimize ${rowData.unique_id}?`,
        context: { assetIds: [rowData.unique_id] },
      });
    },
    [openPanel],
  );

  const tableSearchFunction = useCallback(
    (searchVal: string, row: ModelRuns) => {
      const searchColumns = ['unique_id', 'database_name', 'schema', 'name'];
      return searchColumns.some((col) =>
        getValue(row, col)
          ?.toString()
          .toLowerCase()
          .includes(searchVal.toLowerCase()),
      );
    },
    [],
  );

  const onScrollToBottom = useCallback(() => {
    if (fetchNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  return (
    <Stack sx={{ width: '100%', height: '100%', p: 3, position: 'relative' }}>
      <Stack
        ref={topContentRef}
        sx={{
          mb: 3,
          gap: 3,
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Breadcrumbs items={breadcrumbs} />
          <DaysBack>
            Last {isSaaS ? DEFAULT_DAYS_BACK : reportData?.days_back} Days
          </DaysBack>
        </Stack>
      </Stack>

      <TableWrapper>
        <TableHeader>
          <Flex gap="24px">
            <TableFilters
              data={tableData}
              columns={columns}
              urlParamsPrefix={MODEL_RUNS_TABLE_FILTERS}
              search={search}
              onSearch={setSearch}
              clearFiltersChipLabel={`All (${Humanize.intComma(
                tableData.length,
              )})`}
              disabled={isLoading}
            />
          </Flex>
          <ToggleColumnsMenu columns={columns} localStorageKey="model_runs" />
        </TableHeader>
        <Table
          data={tableData}
          columns={columns}
          search={search}
          searchFunc={isSaaS ? undefined : tableSearchFunction}
          expandedRowRenderer={(rowData) => (
            <ModelRunsExpandedRow
              rowData={rowData}
              onOpenAiChat={handleOpenAiChat}
              aiOptimizationEnabled={!!account?.ai_features_enabled}
            />
          )}
          defaultSortKey="last_exec_time"
          defaultSortDirection="desc"
          scrollItemId={selectedRunId}
          idKey="unique_id"
          urlParamsPrefix={MODEL_RUNS_TABLE_FILTERS}
          localStorageKey="model_runs"
          emptyStateRenderer={TableEmptyState}
          isLoading={isLoading}
          onScrollToBottom={isSaaS ? onScrollToBottom : undefined}
          isControlled={isSaaS}
          isFilterOptionsLoading={isFilterOptionsFetching}
          bottomScrollPadding={isSaaS ? 100 : undefined}
        />
      </TableWrapper>
      {isSaaS && <FloatingActionsMenu />}
    </Stack>
  );
};

export default ModelRunsPage;
